import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  searchCategories as searchCategoriesAPI,
} from "../../services/categoryService";
import { Category } from "../../types";
import { ERROR_MESSAGES } from "../../utils/constants";
import { ErrorToaster, SuccessToaster } from "../../helpers/toasterHelper";

interface CategoryState {
  categories: Category[];
  loading: boolean;
  error: string | null;
}

const initialState: CategoryState = {
  categories: [],
  loading: false,
  error: null,
};

// Async thunk for fetching categories
export const fetchCategories = createAsyncThunk<
  Category[],
  void,
  { rejectValue: { message: string } }
>("category/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await getAllCategories();
    return response.data || [];
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED,
    });
  }
});

export const addCategory = createAsyncThunk(
  "category/addCategory",
  async (
    {
      categoryName,
      showSuccess,
    }: { categoryName: string; showSuccess?: boolean },
    thunkAPI
  ) => {
    try {
      const response = await createCategory(categoryName);
      if (showSuccess) {
        SuccessToaster("Category created successfully");
      }
      thunkAPI.dispatch(fetchCategories());
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to create category";
      ErrorToaster(errorMessage);
      return thunkAPI.rejectWithValue(
        errorMessage
      );
    }
  }
);

export const editCategory = createAsyncThunk(
  "category/editCategory",
  async ({ categoryId, categoryName, showSuccess }: { categoryId: string; categoryName: string; showSuccess?: boolean }, thunkAPI) => {
    try {
      const response = await updateCategory(categoryId, categoryName);
      if (showSuccess) {
        SuccessToaster("Category updated successfully");
      }
      thunkAPI.dispatch(fetchCategories());
      return response;
    } catch (error: any) {
      ErrorToaster("Failed to update category");
      return thunkAPI.rejectWithValue(error.message || "Failed to update category");
    }
  }
);

export const removeCategory = createAsyncThunk("category/removeCategory", async (categoryId: string, thunkAPI) => {
  try {
    await deleteCategory(categoryId);
    SuccessToaster("Category deleted successfully");
    return categoryId;
  } catch (error: any) {
    ErrorToaster("Failed to delete category");
    return thunkAPI.rejectWithValue(error.message || "Failed to delete category");
  }
});

export const searchCategories = createAsyncThunk<
  Category[],
  string,
  { rejectValue: { message: string } }
>("category/searchCategories", async (keyword, { rejectWithValue }) => {
  try {
    const response = await searchCategoriesAPI(keyword);
    return response.data || [];
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Failed to search categories",
    });
  }
});

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    setCategories: (state, action: PayloadAction<Category[]>) => {
      state.categories = action.payload;
    },
    clearCategories: (state) => {
      state.categories = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(addCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(editCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(removeCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter((cat: any) => cat.categoryId !== action.payload);
      })
      .addCase(removeCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(searchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(searchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to search categories";
      });
  },
});

export const { setCategories, clearCategories } = categorySlice.actions;
export default categorySlice.reducer;
export const categorySelector = (state: RootState) => state.category;
export const categoriesSelector = (state: RootState) => state.category.categories;
